import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { Config } from '../core/Config';
import { Input } from '../core/Input';
import { FPSCamera } from './FPSCamera';

export enum CameraMode {
  OVERVIEW,
  FIRST_PERSON
}

export class Camera {
  private camera: THREE.PerspectiveCamera;
  private controls: OrbitControls;
  private input!: Input;
  private fpsCamera!: FPSCamera;
  private currentMode: CameraMode = CameraMode.FIRST_PERSON;
  private lastPosition: THREE.Vector3 = new THREE.Vector3();
  private lastTarget: THREE.Vector3 = new THREE.Vector3();
  
  constructor() {
    // Create the camera
    this.camera = new THREE.PerspectiveCamera(
      Config.FOV,
      window.innerWidth / window.innerHeight,
      Config.NEAR_PLANE,
      Config.FAR_PLANE
    );
    
    // Set initial camera position
    this.camera.position.set(0, Config.CAMERA_DEFAULT_HEIGHT, 0);
    this.camera.lookAt(0, 0, 0);
    
    // Set up orbit controls
    this.controls = new OrbitControls(this.camera, document.body);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.screenSpacePanning = false;
    this.controls.minDistance = Config.CAMERA_MIN_HEIGHT;
    this.controls.maxDistance = Config.CAMERA_MAX_HEIGHT;
    this.controls.maxPolarAngle = Math.PI / 2;
    
    console.log('Camera system initialized');
  }
  
  public setInput(input: Input): void {
    this.input = input;
    
    // Create first-person camera after input is set
    this.fpsCamera = new FPSCamera(this.camera, this.input);
    
    // Initialize in first-person mode
    this.initializeFirstPersonMode();
  }
  
  private initializeFirstPersonMode(): void {
    // Save current position for returning to overview
    this.lastPosition.copy(this.camera.position);
    this.lastTarget.copy(this.controls.target);
    
    // Set camera to a reasonable player position - using terrain center or a specific location
    this.camera.position.set(50, Config.FPS_CAMERA.PLAYER_HEIGHT, 50);
    this.camera.lookAt(150, Config.FPS_CAMERA.PLAYER_HEIGHT, 50); // Look forward
    
    // Disable orbit controls
    this.controls.enabled = false;
    
    // Enable first-person controls
    this.fpsCamera.enable();
    
    console.log('Started in first-person camera mode');
  }
  
  public update(deltaTime: number): void {
    // Check for camera mode switch
    if (this.input.isKeyPressed('c')) {
      console.log('C key pressed - toggling camera mode');
      this.toggleCameraMode();
    }
    
    if (this.currentMode === CameraMode.OVERVIEW) {
      // Update orbit controls
      this.controls.update();
    } else {
      // Update first-person controls
      this.fpsCamera.update(deltaTime);
    }
  }
  
  private toggleCameraMode(): void {
    if (this.currentMode === CameraMode.OVERVIEW) {
      // Save current camera position and target for returning later
      this.lastPosition.copy(this.camera.position);
      this.lastTarget.copy(this.controls.target);
      
      // Switch to first person
      this.currentMode = CameraMode.FIRST_PERSON;
      this.controls.enabled = false;
      
      // Set camera to a reasonable player position - using terrain center or a specific location
      this.camera.position.set(50, Config.FPS_CAMERA.PLAYER_HEIGHT, 50);
      this.camera.lookAt(150, Config.FPS_CAMERA.PLAYER_HEIGHT, 50); // Look forward
      
      // Enable first-person controls
      this.fpsCamera.enable();
      
      // Notify weapon system that we're in first-person mode (via Engine)
      if (window.engine && typeof window.engine.getWeaponSystem === 'function') {
        const weaponSystem = window.engine.getWeaponSystem();
        if (weaponSystem) {
          weaponSystem.showUI();
        }
      }
      
      console.log('Switched to first-person camera');
    } else {
      // Switch back to overview
      this.currentMode = CameraMode.OVERVIEW;
      
      // Disable first-person controls
      this.fpsCamera.disable();
      
      // Restore previous camera position and target
      this.camera.position.copy(this.lastPosition);
      this.controls.target.copy(this.lastTarget);
      this.controls.enabled = true;
      
      // Notify weapon system that we're in overview mode (via Engine)
      if (window.engine && typeof window.engine.getWeaponSystem === 'function') {
        const weaponSystem = window.engine.getWeaponSystem();
        if (weaponSystem) {
          weaponSystem.hideUI();
        }
      }
      
      console.log('Switched to overview camera');
    }
  }
  
  public onResize(width: number, height: number): void {
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }
  
  public getThreeCamera(): THREE.PerspectiveCamera {
    return this.camera;
  }
  
  public getPosition(): THREE.Vector3 {
    return this.camera.position.clone();
  }
  
  public setPosition(x: number, y: number, z: number): void {
    this.camera.position.set(x, y, z);
  }
  
  public lookAt(x: number, y: number, z: number): void {
    this.camera.lookAt(x, y, z);
  }
  
  public getCameraMode(): CameraMode {
    return this.currentMode;
  }
  
  /**
   * Apply mouse look directly with delta values
   * Used for mobile joystick controls
   * @param deltaX The horizontal look delta
   * @param deltaY The vertical look delta
   */
  public applyMouseLook(deltaX: number, deltaY: number): void {
    // Only process if in first person mode
    if (this.currentMode !== CameraMode.FIRST_PERSON) return;
    
    // Since we can't directly use the controls, we'll modify the camera rotation directly
    // This is a simplified approach that works for basic mobile controls
    
    // Apply rotation around Y axis (left/right)
    if (deltaX !== 0) {
      this.camera.rotateY(-deltaX * 0.05);
    }
    
    // Apply rotation around X axis (up/down)
    if (deltaY !== 0) {
      // Get the current pitch (rotation around X axis)
      const euler = new THREE.Euler().setFromQuaternion(this.camera.quaternion, 'YXZ');
      const currentPitch = euler.x;
      
      // Calculate the new pitch
      const newPitch = currentPitch - deltaY * 0.05;
      
      // Limit pitch to prevent looking straight up or down (or backward)
      // Typically ±85 degrees (±1.48 radians) is a good constraint
      const maxPitch = 1.48; // About 85 degrees
      const minPitch = -1.48; // About -85 degrees
      
      // Only apply rotation if within limits
      if (newPitch < maxPitch && newPitch > minPitch) {
        // Create a rotation axis perpendicular to the camera's forward direction
        const rotationAxis = new THREE.Vector3(1, 0, 0);
        // Apply rotation around local X axis
        this.camera.rotateOnAxis(rotationAxis, -deltaY * 0.05);
      }
    }
  }
}
